// Department Dashboard (Module H) — one calm view: this week at a glance + a simple project board.
// TEAM-TIER (Tier 1): coordination, never quality, never coaching. No gamification — status is a
// board column (a state), never a score/ranking. Late = a quiet time, never a red alarm.
// Mobile-first / one-thumb: a card MOVES via a status <select>, not drag. Owner-only edits.

import { api } from "../api";
import { esc } from "../dom";
import { me } from "../state";
import { statusLabel, statusClass, type AssessmentRow } from "./tests";
import type { WeekClock } from "../clock";
import { pageHeader } from "../ui";

type LessonStatus = "not_started" | "in_progress" | "submitted" | "approved";
type EntryType = "teaching" | "test" | "event" | "holiday" | "training";
type ProjectStatus = "backlog" | "doing" | "review" | "done";

interface WeekLesson { teacher_name: string; class_name: string; topic: string; status: LessonStatus; on_time: boolean | null; deadline: string }
interface WeekCal { entry_date: string; label: string; type: EntryType }
interface Dashboard {
  term: { name: string } | null;
  week_no: number | null;
  lessons: WeekLesson[];
  calendar: WeekCal[];
}
interface Project { id: string; title: string; owner_id: string; owner_name: string; status: ProjectStatus; due: string | null }

const COLUMNS: { key: ProjectStatus; label: string }[] = [
  { key: "backlog", label: "Backlog" },
  { key: "doing", label: "Doing" },
  { key: "review", label: "Review" },
  { key: "done", label: "Done" },
];

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MO = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function shortDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  return `${WD[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${d} ${MO[m - 1]}`;
}

// Neutral, non-shaming. Approved/Submitted are calm confirmations; a late submit shows its quiet time.
function lessonChip(l: WeekLesson): string {
  if (l.status === "approved") return `<span class="chip approved">Approved ✓</span>`;
  if (l.status === "submitted") {
    const late = l.on_time === false ? ` <span class="muted">(submitted late)</span>` : "";
    return `<span class="chip submitted">Submitted</span>${late}`;
  }
  return `<span class="chip">Draft</span>`;
}

function statusOptions(selected: ProjectStatus): string {
  return COLUMNS.map((c) => `<option value="${c.key}"${c.key === selected ? " selected" : ""}>${c.label}</option>`).join("");
}

export async function renderDashboard(app: HTMLElement): Promise<void> {
  const myId = me()?.id ?? "";

  // Page-local state (closure; reset when the page is re-entered).
  let dash: Dashboard = { term: null, week_no: null, lessons: [], calendar: [] };
  let projects: Project[] = [];
  let clock: WeekClock | null = null;
  let drafted: AssessmentRow[] = []; // this member's own drafted papers — the best a non-Lead can see
  let renamingId: string | null = null;
  let confirmRemoveId: string | null = null;
  let err: string | null = null;
  const isLead = me()?.auth_role === "lead";

  async function refresh(): Promise<void> {
    const [d, p, c, a] = await Promise.all([
      api<Dashboard>("/api/dashboard"),
      api<{ projects: Project[] }>("/api/projects"),
      // The whole department's picture (every grade at once) — a team-tier aggregate, never a
      // per-teacher one (P1b Job 3).
      api<WeekClock | { clock: null }>("/api/clock?grade=all").catch(() => ({ clock: null }) as { clock: null }),
      // scope=all needs the Lead; a teacher falls back to their own drafts, which is still useful
      // ("have I started this paper yet") even without seeing the whole team's.
      api<{ assessments: AssessmentRow[] }>(`/api/assessments?scope=${isLead ? "all" : "mine"}`).catch(() => ({ assessments: [] })),
    ]);
    dash = d;
    projects = p.projects;
    clock = "week_no" in c ? c : null;
    drafted = a.assessments;
  }

  // "Papers due this week" — one line per test × grade (a multi-grade test like "G9 & G12" makes
  // two lines), each carrying the SAME draft/notes/TOS-OK/checked label the designer's own list
  // uses (tests.ts's statusLabel/statusClass) — never a re-derived or simplified status.
  function papersDueHtml(): string {
    if (!clock || clock.tests.length === 0) return "";
    const lines = clock.tests
      .flatMap((test) => (test.grades ?? [null]).map((g) => ({ test, grade: g })))
      .map(({ test, grade }) => {
        const match = drafted.find((a) => a.test_date === test.date && (grade === null || a.grade === grade));
        const chip = match
          ? `<span class="chip ${statusClass(match)}">${statusLabel(match)}</span>`
          : `<span class="chip">Not started</span>`;
        return `<div class="cal-row"><span class="cal-date">${esc(shortDate(test.date))}</span>` +
          `<span class="cal-label">${esc(test.label)}${grade ? ` · G${grade}` : ""} <span class="muted">· paper due ${esc(shortDate(test.paper_due))}</span></span>${chip}</div>`;
      });
    return `<h2>Papers due this week</h2><div id="papers-due">${lines.join("")}</div>`;
  }

  // ── "This week" section ────────────────────────────────────────────────────────
  function thisWeekHtml(): string {
    const calHtml = dash.calendar.length
      ? dash.calendar
          .map(
            (e) =>
              `<div class="cal-row"><span class="cal-date">${esc(shortDate(e.entry_date))}</span>` +
              `<span class="cal-label">${esc(e.label)}</span><span class="chip ${e.type}">${e.type}</span></div>`,
          )
          .join("")
      : `<p class="empty">Nothing on the calendar this week.</p>`;

    const lessonsHtml = dash.lessons.length
      ? dash.lessons
          .map(
            (l) =>
              `<div class="card"><strong>${esc(l.class_name)}</strong> · ${esc(l.topic)} ` +
              `<span class="muted">— ${esc(l.teacher_name)} · due ${esc(shortDate(l.deadline.slice(0, 10)))}</span>` +
              `<div style="margin-top:6px">${lessonChip(l)}</div></div>`,
          )
          .join("")
      : `<p class="empty">No lessons planned across the team yet this week.</p>`;

    return `
      <p class="hint">${esc(dash.term?.name ?? "")}${dash.week_no ? ` · Week ${dash.week_no}` : ""}</p>
      <div class="card"><strong>This week at school</strong>${calHtml}</div>
      <h2>Lessons this week</h2>
      ${lessonsHtml}
      ${papersDueHtml()}`;
  }

  // ── Project board ────────────────────────────────────────────────────────────────
  function projectCard(p: Project): string {
    const owned = p.owner_id === myId;
    const dueHtml = p.due ? `<div class="muted" style="font-size:var(--text-sm)">Due ${esc(shortDate(p.due))}</div>` : "";

    if (owned && renamingId === p.id) {
      return `<div class="card proj-card" data-id="${esc(p.id)}">
        <input class="rename-input" value="${esc(p.title)}" aria-label="Project title" />
        <div class="row-actions">
          <button class="btn sm" type="button" data-act="save" data-id="${esc(p.id)}">Save</button>
          <button class="btn sm secondary" type="button" data-act="cancel">Cancel</button>
        </div>
      </div>`;
    }

    const ownerLine = owned ? `<span class="muted">You</span>` : `<span class="muted">${esc(p.owner_name)}</span>`;

    if (!owned) {
      // Read-only for non-owners (owner-only edit). Column grouping already shows the status.
      return `<div class="card proj-card" data-id="${esc(p.id)}">
        <strong>${esc(p.title)}</strong>
        <div style="margin-top:4px">${ownerLine}</div>
        ${dueHtml}
      </div>`;
    }

    // Owned: move via a status <select> (not drag), plus quiet Rename / Remove.
    const removeLabel = confirmRemoveId === p.id ? "Remove? Tap again" : "Remove";
    return `<div class="card proj-card" data-id="${esc(p.id)}">
      <strong>${esc(p.title)}</strong>
      <div style="margin-top:4px">${ownerLine}</div>
      ${dueHtml}
      <div class="row-actions" style="margin-top:10px">
        <label class="sr-only" for="mv-${esc(p.id)}">Move</label>
        <select class="proj-move" id="mv-${esc(p.id)}" data-act="move" data-id="${esc(p.id)}" aria-label="Move ${esc(p.title)}">
          ${statusOptions(p.status)}
        </select>
        <button class="linkbtn" type="button" data-act="rename" data-id="${esc(p.id)}">Rename</button>
        <button class="linkbtn quiet" type="button" data-act="remove" data-id="${esc(p.id)}">${removeLabel}</button>
      </div>
    </div>`;
  }

  function boardHtml(): string {
    const byStatus = new Map<ProjectStatus, Project[]>(COLUMNS.map((c) => [c.key, []]));
    for (const p of projects) byStatus.get(p.status)?.push(p);

    const columns = COLUMNS.map((c) => {
      const cards = byStatus.get(c.key)!;
      const inner = cards.length
        ? cards.map(projectCard).join("")
        : `<p class="empty">Nothing here.</p>`;
      return `<div class="board-col"><h2>${c.label} <span class="muted">(${cards.length})</span></h2>${inner}</div>`;
    }).join("");

    return `
      <h2 style="margin-top:var(--space-6)">Projects</h2>
      <p class="hint">A shared board. Move your own cards with the status control — coordination, not a scoreboard.</p>
      ${columns}
      <h2>Add a project</h2>
      <form id="addproject">
        <label for="proj-title">Title</label>
        <input id="proj-title" name="title" placeholder="e.g. Plan the G9 mock exam" required />
        <label for="proj-due">Due (optional)</label>
        <input id="proj-due" name="due" type="date" />
        <div style="height:12px"></div>
        <button class="btn" type="submit">Add project</button>
      </form>`;
  }

  function render(): void {
    const errHtml = err ? `<p class="error">${esc(err)}</p>` : "";
    app.innerHTML = `
      ${pageHeader({ title: "Team board", sub: "What every class is planning this week, and the department's shared board." })}
      <div id="dashroot">
        ${errHtml}
        ${thisWeekHtml()}
        ${boardHtml()}
      </div>`;

    // Fresh container each render → delegated listeners never accumulate across navigations.
    const root = app.querySelector<HTMLElement>("#dashroot")!;
    root.addEventListener("click", onClick);
    root.addEventListener("change", onChange);
    root.addEventListener("submit", onSubmit);
  }

  async function mutate(runFn: () => Promise<void>): Promise<void> {
    err = null;
    try {
      await runFn();
      await refresh();
    } catch (e) {
      err = (e as Error)?.message ?? "Something went wrong.";
    }
    render();
  }

  function onClick(e: Event): void {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-act]");
    if (!btn || btn.tagName === "SELECT") return;
    const act = btn.dataset.act!;
    const id = btn.dataset.id ?? null;

    if (act === "rename") { renamingId = id; confirmRemoveId = null; render(); return; }
    if (act === "cancel") { renamingId = null; render(); return; }
    if (act === "save" && id) {
      const card = (e.target as HTMLElement).closest<HTMLElement>(".proj-card")!;
      const title = card.querySelector<HTMLInputElement>(".rename-input")!.value.trim();
      if (!title) { renamingId = null; render(); return; }
      void mutate(async () => {
        await api(`/api/projects/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ title }) });
        renamingId = null;
      });
      return;
    }
    if (act === "remove" && id) {
      // Two-tap confirm (calm, like the roster) — first tap arms, second removes.
      if (confirmRemoveId !== id) { confirmRemoveId = id; render(); return; }
      void mutate(async () => {
        await api(`/api/projects/${encodeURIComponent(id)}`, { method: "DELETE" });
        confirmRemoveId = null;
      });
      return;
    }
  }

  function onChange(e: Event): void {
    const sel = (e.target as HTMLElement).closest('select[data-act="move"]') as HTMLSelectElement | null;
    if (!sel) return;
    const id = sel.dataset.id!;
    const status = sel.value;
    void mutate(async () => {
      await api(`/api/projects/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ status }) });
    });
  }

  function onSubmit(e: Event): void {
    const form = e.target as HTMLFormElement;
    if (form.id !== "addproject") return;
    e.preventDefault();
    const fd = new FormData(form);
    const title = String(fd.get("title") ?? "").trim();
    const due = String(fd.get("due") ?? "");
    if (!title) return;
    void mutate(async () => {
      await api("/api/projects", { method: "POST", body: JSON.stringify({ title, due: due || null }) });
    });
  }

  await refresh();
  render();
}
